import { groupBy } from 'lodash-es'

import {
  type QueryObserverOptions,
  useQueries,
  useQuery,
  useQueryClient,
} from '@fv/client-core'
import type { FullShipment, Quote } from '@fv/client-types'

import { shipperFetch } from '@/utils/shipperFetch'

import { useQuoteProcessor } from '../useRateModifier'
import { useLoad } from './useLoad'

export const quoteKeys = {
  all: ['quotes'] as const,
  load: (id?: string, version?: number) =>
    [...quoteKeys.all, id ?? null, version ?? null] as const,
  rerated: (loadId?: string, version?: number) =>
    [
      'rerated-quote',
      ...(loadId ? [loadId] : []),
      ...(version ? [version] : []),
    ] as const,
}

async function fetchQuotes(loadId?: string): Promise<Quote[]> {
  if (!loadId) throw new Error('`loadId` required to fetch quotes')
  return shipperFetch<Quote[]>(`/shipments/${loadId}/quotes`)
}

type Opts = {
  interval?: QueryObserverOptions<
    Quote[],
    unknown,
    Quote[],
    Quote[]
  >['refetchInterval']
  enabled?: boolean
}
export function useQuotes(loadId?: string, opts?: Opts) {
  const client = useQueryClient()
  const quoteProcessor = useQuoteProcessor()
  const { interval, enabled } = opts ?? { enabled: true }
  const load = useLoad(loadId, {
    enabled: Boolean(loadId) && enabled,
  })
  const key = quoteKeys.load(loadId, load.data?.currentQuoteVersion)
  const query = useQuery<Quote[]>(key, () => fetchQuotes(loadId), {
    enabled: Boolean(loadId) && enabled,
    refetchInterval: interval,
    select(quotes) {
      return quoteProcessor(
        quotes,
        load.data?.workflow,
        load.data?.exchangeRates,
      )
    },
  })
  const invalidate = () => client.invalidateQueries({ queryKey: key })
  return {
    ...query,
    invalidate,
  }
}

export function useCachedQuotes(loadId?: string, version?: number) {
  return useQuery({
    queryKey: quoteKeys.load(loadId, version),
    queryFn: () => fetchQuotes(loadId),
    enabled: false,
    staleTime: Infinity,
  })
}

// Used to fetch quotes for multiple loads at once
export function useQuoteRequestQuotes(loads: FullShipment[]) {
  const data: { [key: string]: Quote[] } = {}
  const uniqueLoads = getUniqueLoadWithVersions(loads)

  const quoteQueries = useQueries({
    queries: uniqueLoads.map(({ loadId, version }) => ({
      enabled: Boolean(loadId),
      queryFn: async () => {
        try {
          return await fetchQuotes(loadId)
        } catch (_e) {
          return []
        }
      },
      queryKey: quoteKeys.load(loadId, version),
      retry: false,
    })),
  })

  uniqueLoads.forEach(({ loadId }, index) => {
    data[loadId] = quoteQueries[index].data ?? []
  })

  return {
    data,
    isLoading: quoteQueries.some(q => q.isLoading && q.isFetching),
  }
}

const getUniqueLoadWithVersions = (loads: FullShipment[]) => {
  const uniqueLoads = groupBy(loads, l => l.loadId)
  return Object.entries(uniqueLoads).map(([id, loads]) => ({
    loadId: id,
    version: loads[0]?.currentQuoteVersion,
  }))
}
